import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Languages, Mail, Lock, User, Eye, EyeOff, ArrowRight, Loader2, CheckCircle, PartyPopper } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);

  const { loginWithGoogle } = useAuth();

  const handleGoogleSuccess = async (tokenResponse) => {
    setGoogleLoading(true);
    setError('');
    try {
      await loginWithGoogle(tokenResponse.access_token);
      navigate('/editor');
    } catch (err) {
      setError(err.error || 'Google ile kayıt/giriş başarısız. Lütfen tekrar deneyin.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => setError('Google girişi iptal edildi veya başarısız oldu.'),
    flow: 'implicit',
    ux_mode: 'popup',
  });

  const passwordRequirements = [
    { met: formData.password.length >= 8, text: 'En az 8 karakter' },
    { met: /[A-Z]/.test(formData.password), text: 'Büyük harf' },
    { met: /[a-z]/.test(formData.password), text: 'Küçük harf' },
    { met: /[0-9]/.test(formData.password), text: 'Rakam' },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Lütfen tüm alanları doldurun.');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Şifre en az 8 karakter olmalıdır.');
      setLoading(false);
      return;
    }

    try {
      await register({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password
      });
      setRegistered(true);
    } catch (err) {
      setError(err.error || 'Kayıt sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="min-h-screen bg-soft-blue flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/90 rounded-2xl shadow-lg border border-blue-100 p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PartyPopper className="w-8 h-8 text-blue-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-3">Kaydınız Tamamlandı! 🎉</h1>
            <p className="text-slate-600 text-sm mb-3">
              <strong className="text-blue-500">{formData.email}</strong> adresine doğrulama maili gönderdik.
            </p>
            <p className="text-xs text-slate-400 mb-5">Hesabınızı aktif etmek için emailinizi kontrol edin.</p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 py-2.5 px-5 bg-gradient-to-r from-blue-400 to-indigo-400 text-white rounded-xl font-medium text-sm hover:shadow-md transition-all"
            >
              Giriş Sayfasına Git
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-soft-blue flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <img src="/logo192.png" alt="CeviriPro Logo" className="w-10 h-10 object-contain drop-shadow-sm" />
          <span className="text-xl font-bold text-gradient-blue">
            ÇeviriPro
          </span>
        </div>

        {/* Card */}
        <div className="bg-white/90 rounded-2xl shadow-lg border border-blue-100 p-6">
          <div className="text-center mb-5">
            <h1 className="text-xl font-bold text-slate-900 mb-1">
              Hesap Oluşturun
            </h1>
            <p className="text-slate-600 text-sm">
              ÇeviriPro'yu kullanmaya başlayın
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Ad & Soyad */}
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ad</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400">
                    <User className="w-3.5 h-3.5" />
                  </div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Adınız"
                    className="w-full pl-9 pr-3 py-2.5 border border-blue-100 rounded-xl focus:border-blue-300 focus:outline-none transition-all bg-white text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Soyad</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Soyadınız"
                  className="w-full px-3 py-2.5 border border-blue-100 rounded-xl focus:border-blue-300 focus:outline-none transition-all bg-white text-sm"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                E-posta Adresi
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400">
                  <Mail className="w-4 h-4" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ornek@email.com"
                  className="w-full pl-10 pr-3 py-2.5 border border-blue-100 rounded-xl focus:border-blue-300 focus:outline-none transition-all bg-white text-sm"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Şifre
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400">
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Şifrenizi girin"
                  className="w-full pl-10 pr-10 py-2.5 border border-blue-100 rounded-xl focus:border-blue-300 focus:outline-none transition-all bg-white text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-500"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {formData.password && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className={`flex items-center gap-1 text-xs ${req.met ? 'text-blue-500' : 'text-slate-400'}`}>
                      <CheckCircle className={`w-3 h-3 ${req.met ? 'text-blue-400' : 'text-slate-300'}`} />
                      {req.text}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Şifre Tekrar
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400">
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Şifrenizi tekrar girin"
                  className="w-full pl-10 pr-3 py-2.5 border border-blue-100 rounded-xl focus:border-blue-300 focus:outline-none transition-all bg-white text-sm"
                />
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="mt-1 text-xs text-red-500">Şifreler eşleşmiyor</p>
              )}
            </div>

            {/* Terms */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 mt-0.5 text-blue-500 border-blue-200 rounded focus:ring-blue-400"
                required
              />
              <span className="text-xs text-slate-600">
                <a href="#" className="text-blue-500 hover:underline">Kullanım şartlarını</a> kabul ediyorum
              </span>
            </label>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-blue-400 to-indigo-400 text-white rounded-xl font-medium hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Hesap oluşturuluyor...
                </>
              ) : (
                <>
                  Kaydol
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="my-5 flex items-center gap-4">
            <div className="flex-1 border-t border-blue-50"></div>
            <span className="text-xs text-slate-400">veya</span>
            <div className="flex-1 border-t border-blue-50"></div>
          </div>

          {/* Social Register */}
          <button
            type="button"
            onClick={() => googleLogin()}
            disabled={googleLoading}
            className="w-full py-2.5 bg-white border border-blue-100 rounded-xl font-medium text-slate-600 hover:bg-blue-50 transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-60"
          >
            {googleLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Kayıt yapılıyor...</>
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Google ile Kaydol
              </>
            )}
          </button>
        </div>

        {/* Login Link */}
        <div className="mt-5 text-center">
          <p className="text-slate-600 text-sm">
            Zaten hesabınız var mı?{' '}
            <Link to="/login" className="text-blue-500 hover:text-blue-600 font-medium">
              Giriş yapın
            </Link>
          </p>
        </div>

        {/* Back to Home */}
        <div className="mt-3 text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-blue-500">
            ← Ana sayfaya dön
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;